import React, { useEffect, useState } from "react";
import { Box, Typography, Divider, Button, Switch, IconButton, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DiscountForm from "./DiscountForm";
import ButtonSquare from "../../components/ui/ButtonSquare";

function DiscountsSettings({ access_token }) {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editDiscount, setEditDiscount] = useState(null);

  const fetchDiscounts = () => {
    setLoading(true);
    Api.get(Urls.GET_DISCOUNTS, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then((res) => {
        setDiscounts(res?.data?.data || res?.data?.data?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to fetch discounts");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchDiscounts();
    // eslint-disable-next-line
  }, []);

  const handleToggle = (discount) => {
    Api.patch(Urls.TOGGLE_DISCOUNT.replace(":id", discount.id), {}, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then(() => {
        toast.success("Discount status updated");
        // Met à jour localement l'état du discount togglé
        setDiscounts((prev) =>
          prev.map((d) =>
            d.id === discount.id ? { ...d, is_active: !d.is_active } : d
          )
        );
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to update status");
      });
  };

  const handleDelete = (discount) => {
    if (!window.confirm("Are you sure you want to delete this discount?")) return;
    Api.delete(Urls.DELETE_DISCOUNT.replace(":id", discount.id), {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then(() => {
        toast.success("Discount deleted");
        fetchDiscounts();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to delete discount");
      });
  };

  // Ajoute ou met à jour un discount localement
  const upsertDiscount = (discount) => {
    setDiscounts((prev) => {
      const exists = prev.find((d) => d.id === discount.id);
      if (exists) {
        // update
        return prev.map((d) => (d.id === discount.id ? discount : d));
      } else {
        // create
        return [discount, ...prev];
      }
    });
  };

  return (
    <Box mt={4}>
      <Typography fontSize="18px" fontWeight="500" color="#4B465C" mb={2}>
        Discounts
      </Typography>
      <Divider />
      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Box mt={2}>
          {discounts.length === 0 ? (
            <Typography>No discounts configured yet.</Typography>
          ) : (
            discounts.map((discount) => (
              <Box key={discount.id} display="flex" alignItems="center" gap={2} mb={2} p={2} border={1} borderColor="#eee" borderRadius={2}>
                <Box flex={1}>
                  <Typography fontWeight="bold">{discount.name}</Typography>
                  <Typography variant="body2">Type: {discount.type}</Typography>
                  <Typography variant="body2">Active: {discount.is_active ? "Yes" : "No"}</Typography>
                  <Tooltip title={JSON.stringify(discount.config, null, 2)} placement="top">
                    <Typography variant="body2" sx={{ cursor: "pointer", color: "#1976d2" }}>View config</Typography>
                  </Tooltip>
                </Box>
                <Switch checked={discount.is_active} onChange={() => handleToggle(discount)} />
                <ButtonSquare action="edit_outlined" onClick={() => setEditDiscount(discount)} />
                <ButtonSquare action="delete_red" onClick={() => handleDelete(discount)} />
              </Box>
            ))
          )}
        </Box>
      )}
      <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={() => setFormOpen(true)}>
        Add Discount
      </Button>
      <DiscountForm
        open={formOpen || Boolean(editDiscount)}
        onClose={() => { setFormOpen(false); setEditDiscount(null); }}
        onSuccess={(discount) => {
          upsertDiscount(discount);
          setFormOpen(false);
          setEditDiscount(null);
        }}
        access_token={access_token}
        initialValues={editDiscount}
      />
    </Box>
  );
}

export default DiscountsSettings; 